export const PROJECT_TYPES = [
  { value: 'financial', label: 'Financial system', hint: 'Payments, trading, reconciliation, lending' },
  { value: 'saas', label: 'SaaS platform', hint: 'Multi-tenant product, billing, integrations' },
  { value: 'data', label: 'Data platform', hint: 'Pipelines, warehousing, analytics' },
  { value: 'healthcare', label: 'Healthcare solution', hint: 'Telehealth, clinical workflow, claims and authorisations' },
  { value: 'vibe-app', label: 'Scan or productize an app', hint: 'An app built fast that needs review or a path to production' },
  { value: 'modernise', label: 'Modernise a legacy system', hint: 'Move, split or rebuild what already runs' },
  { value: 'other', label: 'Something else', hint: 'Tell us in your note' },
] as const

export const STAGES = [
  { value: 'idea', label: 'New idea', hint: 'Nothing built yet' },
  { value: 'build', label: 'Build in progress', hint: 'Need engineers to add or fix' },
  { value: 'live', label: 'Live product', hint: 'Need it stabilised or scaled' },
] as const

export const TIMELINES = [
  { value: 'now', label: 'Starting this quarter' },
  { value: 'soon', label: 'In one to three months' },
  { value: 'exploring', label: 'Still exploring' },
] as const

export type BriefInput = {
  projectType: string
  stage: string
  timeline: string
  name: string
  email: string
  company: string
  note: string
}

export const EMPTY_BRIEF: BriefInput = {
  projectType: '',
  stage: '',
  timeline: '',
  name: '',
  email: '',
  company: '',
  note: '',
}

export const BRIEF_EMAIL = 'hello@rozitech.com'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function validateBrief(input: BriefInput): Partial<Record<keyof BriefInput, string>> {
  const errors: Partial<Record<keyof BriefInput, string>> = {}
  if (!input.projectType) errors.projectType = 'Choose the closest project type.'
  if (!input.stage) errors.stage = 'Choose where the project is today.'
  if (!input.timeline) errors.timeline = 'Choose a rough timeline.'
  if (!input.name.trim()) errors.name = 'Add your name so we know who to reply to.'
  if (!EMAIL_PATTERN.test(input.email.trim())) errors.email = 'Add a work email we can reply to.'
  return errors
}

function label<T extends readonly { value: string; label: string }[]>(list: T, value: string): string {
  return list.find((item) => item.value === value)?.label ?? value
}

export function buildBriefMailto(input: BriefInput): string {
  return `mailto:${BRIEF_EMAIL}?subject=${encodeURIComponent(briefSubject(input))}&body=${encodeURIComponent(briefBody(input))}`
}

export function briefSubject(input: BriefInput): string {
  const who = input.company.trim() || input.name.trim()
  return `Project brief: ${who}`
}

export function briefBody(input: BriefInput): string {
  return [
    `Name: ${input.name.trim()}`,
    `Company: ${input.company.trim() || 'Not given'}`,
    `Work email: ${input.email.trim()}`,
    '',
    `Project type: ${label(PROJECT_TYPES, input.projectType)}`,
    `Where it stands: ${label(STAGES, input.stage)}`,
    `Timeline: ${label(TIMELINES, input.timeline)}`,
    '',
    'What we are building:',
    input.note.trim() || 'Not given yet.',
  ].join('\n')
}

// Posts the brief to a form-handling endpoint that emails hello@rozitech.com.
export async function sendBrief(
  input: BriefInput,
  endpoint: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      subject: briefSubject(input),
      replyTo: input.email.trim(),
      body: briefBody(input),
    }),
  })
  if (!res.ok) throw new Error(`Brief not sent (${res.status})`)
}
